import React from 'react';
import style from './style.module.scss';
import { joinNames } from '../helpers';
import { ButtonRowContainer, Hero } from '../Container';
import { Headline, Paragraph } from '../Text';
import Button from '../Button';
import { ArrowLeftIcon } from '../../icons';
import {IconComponent, IconName} from '@n2-common/myPagesUi/components/icons';

export type ActionState = 'ready' | 'disabled' | 'final' | 'completed' | 'pending' | 'invalid';
export type Action<TPayload> = {
    name: string;
    actionName?: string;
    description?: string;
    parameter: TPayload;
    state: ActionState;
    iconName?: IconName;
};

export type ActionListProps<TPayload> = {
    className?: string;
    ui: 'compact' | 'detailed';
    execute: (payload: TPayload) => void;
    actions: Action<TPayload>[];
    completedPlaceholder?: JSX.Element;
};

/**
 * I have typedeffinition on React.forwardRef here in modulescope, to fix React' missing type-support
 * for higher-order function type inference aka generic type inference.
 */
declare module 'react' {
    function forwardRef<T, P = object>(
        render: (props: P, ref?: React.Ref<T>) => React.ReactElement | null
    ): (props: P & React.RefAttributes<T>) => React.ReactElement | null;
}

function ActionIcon({ iconName, state }: Pick<Action<unknown>, 'iconName' | 'state'>) {
    if(['completed', 'final'].includes(state)){
        return <IconComponent iconName={'checkmarkIcon'}  />;
    }
    if(iconName) {
        return <IconComponent iconName={iconName} /> 
    }
    return null;
}

export default React.forwardRef(function ActionList<TPayload>(
    { actions, execute, completedPlaceholder, className, ui }: ActionListProps<TPayload>,
    ref?: React.ForwardedRef<HTMLDivElement>
): JSX.Element {
    const allCompleted = actions.every((a) => ['completed', 'final'].includes(a.state));

    return (
        <div ref={ref}>
            {ui == 'compact' ? (
                <ul className={joinNames(style.list, className)}>
                    {actions.map((a, i) => (
                        <li
                            data-nameforuitesting={`action_${a.actionName}`}
                            tabIndex={['ready', 'completed'].includes(a.state) ? 0 : undefined}
                            title={a.description}
                            className={joinNames(style.item, style.button, nameFromState(a.state))}
                            role="button"
                            key={i}
                            onClick={['ready', 'completed'].includes(a.state) ? () => execute(a.parameter) : undefined}
                            onKeyPress={['ready', 'completed'].includes(a.state) ? (e) => e.key == 'Enter' && execute(a.parameter) : undefined}
                        >
                            <ActionIcon {...a} />
                            <div className={style.text}>
                                <div>{a.name}</div>
                                {a.description && <div>{a.description}</div>}
                            </div>
                            {['ready', 'completed'].includes(a.state) && <ArrowLeftIcon height={15} />}
                        </li>
                    ))}
                </ul>
            ) : (
                actions.map((action, i) => (
                    <Hero
                        ui={['completed', 'final'].includes(action.state) ? 'ternary' : 'secondary'}
                        key={i}
                        className={joinNames(nameFromState(action.state), style.hero)}
                    >
                        <Headline level={3}>{action.name}</Headline>
                        <Paragraph>{action.description}</Paragraph>
                        <ButtonRowContainer className={style.butonContainer}>
                            {['ready', 'completed'].includes(action.state) && (
                                <Button
                                    className={style.button}
                                    uiType="primary"
                                    onClick={['ready', 'completed'].includes(action.state) ? () => execute(action.parameter) : undefined}
                                >
                                    {action.actionName || 'Ok'}
                                </Button>
                            )}
                            {action.state != 'ready' && <div className={joinNames(style.icon, style.button, nameFromState(action.state))} />}
                        </ButtonRowContainer>
                    </Hero>
                ))
            )}
            {allCompleted && completedPlaceholder}
        </div>
    );
});

function nameFromState(state: ActionState): string | undefined {
    switch (state) {
        case 'ready':
            return style.ready;
        case 'completed':
            return style.completed;
        case 'final':
            return style.final;
        case 'disabled':
            return style.disabled;
        case 'pending':
            return style.pending;
        case 'invalid':
            return style.invalid;

        default:
            return undefined;
    }
}
